import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Switch } from "@headlessui/react";
import AnimatedPage from "../../components/animatedPage/AnimatedPage";

import {
  ChevronUpIcon,
  ChevronDownIcon,
  StarIcon,
} from "@heroicons/react/solid";

import AddVendorModal from "./AddVendorModal";
import Loader from "../../components/loader/Loader";
import CreateFBOModal from "./CreateFBOModal";
import * as api from "./apiService";
import { toast } from "react-toastify";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const MagnifyingGlassIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-5 h-5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
      />
    </svg>
  );
};

const AirportDetails = () => {
  const { airportId } = useParams();

  const [airportDetails, setAirportDetails] = useState({});
  const [loading, setLoading] = useState(true);

  // ===== new: General Info form state =====
  const [airportForm, setAirportForm] = useState({
    is_wet_wash_allowed: false,
    special_notes: "",
  });
  const [savingAirport, setSavingAirport] = useState(false);
  const [airportDirty, setAirportDirty] = useState(false);

  const [fbos, setFbos] = useState([]);
  const [totalFbos, setTotalFbos] = useState(0);
  const [loadingFbos, setLoadingFbos] = useState(false);
  const [fboSearchText, setFboSearchText] = useState("");
  const [fboAlreadyAdded, setFboAlreadyAdded] = useState(false);

  const [availableFbos, setAvailableFbos] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);

  const [isCreateFboModalOpen, setCreateFboModalOpen] = useState(false);
  const [isAddVendorModalOpen, setAddVendorModalOpen] = useState(false);

  useEffect(() => {
    let timeoutID = setTimeout(() => {
      searchFbos();
    }, 500);

    return () => {
      clearTimeout(timeoutID);
    };
  }, [fboSearchText]);

  useEffect(() => {
    getAirportDetails();
    getAvailableFbos();
    getAirportAvailableUsers();
    setFboAlreadyAdded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airportId]);

  const handleToggleCreateFboModal = () => {
    setCreateFboModalOpen(!isCreateFboModalOpen);
  };

  const handleToggleAddVendorModal = () => {
    setAddVendorModalOpen(!isAddVendorModalOpen);
  };

  const searchFbos = async () => {
    setLoadingFbos(true);
    try {
      const request = { name: fboSearchText };
      const { data } = await api.searchFbos(request);

      setFbos(data.results);
      setTotalFbos(data.count);
      setFboAlreadyAdded(false);
    } catch (error) {
      // noop
    } finally {
      setLoadingFbos(false);
    }
  };

  const getAirportAvailableUsers = async () => {
    setLoading(true);

    try {
      const { data } = await api.getAirportAvailableUsers(Number(airportId));
      data.forEach((vendor) => (vendor.open = true));
      setAvailableVendors(data);
    } catch (err) {
      toast.error("Unable to get available users");
    }

    setLoading(false);
  };

  const getAirportDetails = async () => {
    setLoading(true);
    try {
      const { data } = await api.getAirportDetails(airportId);

      setAirportDetails(data);

      // ===== new: seed the General Info form =====
      setAirportForm({
        is_wet_wash_allowed: !!data?.is_wet_wash_allowed,
        special_notes: data?.special_notes || "",
      });
      setAirportDirty(false);
    } catch (err) {
      // noop
    } finally {
      setLoading(false);
    }
  };

  const getAvailableFbos = async () => {
    try {
      const { data } = await api.getAirportAvailableFbos(airportId);
      setAvailableFbos(data);
    } catch (err) {
      toast.error("Unable to get available fbos");
    }
  };

  const addAvailableFbo = async (fboId) => {
    const request = { fbo_id: fboId, airport_id: airportId, action: "add" };

    try {
      const fboExists = availableFbos.find((fbo) => fbo.id === fboId);

      if (fboExists) {
        setFboAlreadyAdded(true);
      } else {
        const { data } = await api.updateAirportAvailableFbo(request);
        setFboAlreadyAdded(false);
        setAvailableFbos([...availableFbos, data]);
      }
    } catch (error) {
      setFboAlreadyAdded(false);
    }
  };

  const deleteAvailableFbo = async (fboId) => {
    try {
      const request = {
        fbo_id: fboId,
        airport_id: airportId,
        action: "delete",
      };
      await api.updateAirportAvailableFbo(request);

      setFboAlreadyAdded(false);
      setAvailableFbos(availableFbos.filter((airport) => airport.id !== fboId));
    } catch (error) {}
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      searchFbos();
    }
  };

  const handleToggleVendor = (vendor) => {
    const newVendors = availableVendors.map((v) => {
      if (v.name === vendor.name) return { ...v, open: !v.open };
      return v;
    });

    setAvailableVendors(newVendors);
  };

  const handleCreateFbo = async (fbo) => {
    const request = {
      airport_id: airportId,
      name: fbo.name,
      is_public: fbo.public,
    };

    try {
      const { data } = await api.createFBO(request);
      setAvailableFbos([...availableFbos, data]);
      toast.success("FBO added!");
    } catch (err) {
      alert("FBO already exists");
      return;
    }

    setCreateFboModalOpen(false);
  };

  const handleAddVendor = async (userId) => {
    const request = { airport_id: Number(airportId), user_id: Number(userId) };

    try {
      await api.addAirportAvailableUser(request);
      toast.success("Vendor added!");
    } catch (err) {
      toast.error("Unable to add vendor");
    }

    setAddVendorModalOpen(false);
    getAirportAvailableUsers();
  };

  const handleRemoveUser = async (userId) => {
    const request = { airport_id: Number(airportId), user_id: Number(userId) };

    try {
      await api.removeAirportAvailableUser(request);

      const newVendors = availableVendors.map((vendor) => {
        const newUsers = vendor.users.filter((user) => user.id !== userId);
        return { ...vendor, users: newUsers };
      });

      setAvailableVendors(newVendors);
      toast.success("User removed");
    } catch (err) {
      toast.error("Unable to remove user");
    }
  };

  const handleToggleSetAsPreferred = async (userId) => {
    const request = { user_id: userId, airport_id: Number(airportId) };

    try {
      await api.togglePreferredProjectManager(request);

      const newVendors = availableVendors.map((vendor) => {
        const newUsers = vendor.users.map((user) => {
          if (user.id === userId) {
            return {
              ...user,
              is_preferred_project_manager: !user.is_preferred_project_manager,
            };
          }
          return { ...user, is_preferred_project_manager: false };
        });

        return { ...vendor, users: newUsers };
      });

      setAvailableVendors(newVendors);
      toast.success("Vendor set as preferred");
    } catch (err) {
      toast.error("Unable to set as preferred");
    }
  };

  // ===== new: General Info handlers =====
  const handleToggleWetWashAllowed = (checked) => {
    setAirportForm((prev) => ({ ...prev, is_wet_wash_allowed: checked }));
    setAirportDirty(true);
  };

  const handleAirportNotesChange = (e) => {
    setAirportForm((prev) => ({ ...prev, special_notes: e.target.value }));
    setAirportDirty(true);
  };

  const handleSaveGeneralInfo = async () => {
    setSavingAirport(true);
    try {
      const payload = {
        is_wet_wash_allowed: !!airportForm.is_wet_wash_allowed,
        special_notes: airportForm.special_notes?.trim() || null,
      };

      // IMPORTANT: assumes you have an apiService method like updateAirportDetails(airportId, payload)
      await api.updateAirportDetails(Number(airportId), payload);

      // optimistic UI update
      setAirportDetails((prev) => ({ ...prev, ...payload }));

      setAirportDirty(false);
      toast.success("Airport details updated.");
    } catch (err) {
      toast.error("Unable to update airport details.");
    } finally {
      setSavingAirport(false);
    }
  };

  return (
    <AnimatedPage>
      {loading && <Loader />}

      {!loading && (
        <div className="xl:px-16 px-4">
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-3xl font-semibold text-gray-900">
              {airportDetails.name}
            </h3>
            <div className="mt-1 text-lg text-gray-500 ">
              {airportDetails.initials}
            </div>
            <div className="mt-1 text-lg text-gray-500 ">
              {airportDetails.timezone}
            </div>
          </div>

          <div className="grid 2xl:grid-cols-1 xl:grid-cols-1 lg:grid-cols-1 md:grid-cols-1 sm:grid-cols-1 xs:grid-cols-1 mt-6 gap-16">
            <div className="">
              {/* ===== NEW: General Info section ===== */}
              <div className="mb-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-xl font-semibold">General Info</div>
                    <div className="text-md text-gray-500 mt-1">
                      Configure airport rules and notes used across job creation
                      and validation.
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-1">
                    {airportDirty && (
                      <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-inset ring-amber-200">
                        Unsaved changes
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleSaveGeneralInfo}
                      disabled={savingAirport || !airportDirty}
                      className={classNames(
                        "inline-flex items-center justify-center rounded-md px-4 py-2 text-md font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto",
                        savingAirport || !airportDirty
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-red-500 hover:bg-red-700 focus:ring-red-500",
                      )}
                    >
                      {savingAirport ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>

                <div className="mt-6 rounded-md border border-gray-200 bg-white p-6">
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {/* Wet Wash Toggle */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <Switch.Group
                        as="div"
                        className="flex items-start justify-between gap-4"
                      >
                        <div className="min-w-0">
                          <Switch.Label className="text-sm font-semibold text-gray-900">
                            Is Wet Wash allowed at this airport?
                          </Switch.Label>
                          <Switch.Description className="mt-1 text-sm text-gray-600">
                            When disabled, exterior wet wash should be blocked
                            or warned for this airport.
                          </Switch.Description>

                          <div className="mt-3">
                            {airportForm.is_wet_wash_allowed ? (
                              <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                Allowed
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200">
                                Not allowed
                              </span>
                            )}
                          </div>
                        </div>

                        <Switch
                          checked={!!airportForm.is_wet_wash_allowed}
                          onChange={handleToggleWetWashAllowed}
                          className={classNames(
                            airportForm.is_wet_wash_allowed
                              ? "bg-sky-500"
                              : "bg-gray-200",
                            "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2",
                          )}
                        >
                          <span
                            aria-hidden="true"
                            className={classNames(
                              airportForm.is_wet_wash_allowed
                                ? "translate-x-5"
                                : "translate-x-0",
                              "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                            )}
                          />
                        </Switch>
                      </Switch.Group>
                    </div>

                    {/* Special Notes */}
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <label className="block text-sm font-semibold text-gray-900">
                        Special Notes
                      </label>
                      <p className="mt-1 text-sm text-gray-600">
                        Visible to internal users and can be used for
                        airport-specific rules or instructions.
                      </p>
                      <textarea
                        rows={6}
                        value={airportForm.special_notes}
                        onChange={handleAirportNotesChange}
                        className="mt-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 text-md"
                        placeholder='e.g., "Wet wash permitted only inside hangar" or "No water usage on ramp"'
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Existing section: Associated Vendors */}
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <div className="text-xl font-semibold">
                    Associated Vendors
                  </div>
                  <div className="text-md text-gray-500 mt-1">
                    Only users from associated vendors will be eligible for job
                    assignments for the selected airport.
                  </div>
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => handleToggleAddVendorModal()}
                    className="inline-flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2
                              text-md font-medium text-white shadow-sm hover:bg-red-700
                              focus:outline-none focus:ring-2 focus:ring-red-500
                              focus:ring-offset-2 sm:w-auto mt-4"
                  >
                    Add Vendor
                  </button>
                </div>
              </div>

              <div className="mt-8">
                {availableVendors.map((vendor) => (
                  <div
                    key={vendor.name}
                    className="border border-gray-200 rounded-md p-6 pb-8 mb-4"
                  >
                    <div
                      className="flex justify-between gap-4 cursor-pointer"
                      onClick={() => handleToggleVendor(vendor)}
                    >
                      <div className="flex-1 font-semibold">{vendor.name}</div>
                      <div>
                        {vendor.open ? (
                          <ChevronUpIcon className="h-5 w-5 relative top-2" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 relative top-2" />
                        )}
                      </div>
                    </div>

                    {vendor.open && (
                      <div className="mt-4">
                        {vendor.users.map((user) => (
                          <div key={user.id} className="relative">
                            <ul className="relative z-0 divide-y divide-gray-200">
                              <li
                                className={classNames(
                                  "my-2 border border-gray-200 rounded-md px-2",
                                  user.is_preferred_project_manager
                                    ? "bg-green-50 border-green-200"
                                    : "",
                                )}
                              >
                                <div
                                  className={classNames(
                                    "relative flex flex-wrap items-center space-x-3 gap-6 py-5",
                                    user.is_preferred_project_manager
                                      ? ""
                                      : "hover:bg-gray-50",
                                  )}
                                >
                                  <div>
                                    <img
                                      className="h-10 w-10 rounded-full"
                                      src={user.profile?.avatar}
                                      alt=""
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <div className="focus:outline-none">
                                      <p className="text-md font-medium text-gray-900">
                                        {user.first_name} {user.last_name}
                                      </p>
                                      <p className="truncate text-sm text-gray-500">
                                        {user.email
                                          ? user.email
                                          : "No email specified"}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex gap-2">
                                    {!user.is_preferred_project_manager && (
                                      <button
                                        onClick={() =>
                                          handleToggleSetAsPreferred(user.id)
                                        }
                                        className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900
                                                  ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:block cursor-pointer"
                                      >
                                        Set as Preferred
                                      </button>
                                    )}

                                    {user.is_preferred_project_manager && (
                                      <div
                                        onClick={() =>
                                          handleToggleSetAsPreferred(user.id)
                                        }
                                        className="inline-flex items-center rounded-md p-2 px-4 text-md font-medium bg-green-100 text-green-700 capitalize cursor-pointer"
                                      >
                                        <StarIcon className="h-4 w-4 mr-2" />
                                        Preferred Vendor
                                      </div>
                                    )}

                                    <button
                                      onClick={() => handleRemoveUser(user.id)}
                                      className="rounded-md bg-white px-2.5 py-1.5 text-sm ml-2 font-semibold text-gray-900
                                                ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:block cursor-pointer"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              </li>
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Associated FBOs */}
            <div className="border-t border-gray-200 pt-4">
              <div className="text-xl font-semibold">Associated FBOs</div>
              <div className="text-md text-gray-500 mt-1">
                Only associated FBOs will be shown when selecting this airport
                at the creation job view.
              </div>

              <div className="mt-8 grid xl:grid-cols-2 lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-1 xs:grid-cols-1 gap-x-8">
                <div
                  className="border border-gray-200 rounded-md p-4"
                  style={{ height: "900px" }}
                >
                  <div className="font-medium text-md">
                    <div className="flex justify-between">
                      <div>
                        All FBOs
                        <span className="bg-gray-100 text-gray-700 ml-2 py-1 px-2 rounded-full text-md font-medium inline-block">
                          {totalFbos}
                        </span>
                      </div>
                      <div>
                        {fboAlreadyAdded && (
                          <div className="text-red-500 text-md relative top-1">
                            FBO already added
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="min-w-0 flex-1 my-2">
                      <label htmlFor="search" className="sr-only">
                        Search
                      </label>
                      <div className="relative rounded-md shadow-sm">
                        <div
                          onClick={() => searchFbos()}
                          className="absolute inset-y-0 left-0 flex items-center pl-3 cursor-pointer"
                        >
                          <MagnifyingGlassIcon
                            className="h-5 w-5 text-gray-400 cursor-pointer"
                            aria-hidden="true"
                          />
                        </div>
                        <input
                          type="search"
                          name="search"
                          id="search"
                          value={fboSearchText}
                          onChange={(event) =>
                            setFboSearchText(event.target.value)
                          }
                          onKeyDown={handleKeyDown}
                          className="block w-full rounded-md border-gray-300 pl-10 focus:border-sky-500 text-md focus:ring-sky-500 font-normal"
                          placeholder="Search name..."
                        />
                      </div>
                    </div>

                    <div
                      className="overflow-y-auto"
                      style={{ maxHeight: "700px" }}
                    >
                      {loadingFbos && (
                        <div className="py-6 text-center text-gray-500">
                          Loading...
                        </div>
                      )}
                      {!loadingFbos &&
                        fbos.map((fbo) => (
                          <div key={fbo.id} className="relative">
                            <ul>
                              <li>
                                <div className="relative flex items-center space-x-3 px-2 py-3 hover:bg-gray-50 rounded-md">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-md text-gray-900 font-normal truncate overflow-ellipsis w-60">
                                      {fbo.name}
                                    </p>
                                  </div>
                                  <div>
                                    <button
                                      type="button"
                                      onClick={() => addAvailableFbo(fbo.id)}
                                      className="inline-flex items-center rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2"
                                    >
                                      Add
                                    </button>
                                  </div>
                                </div>
                              </li>
                            </ul>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>

                <div
                  className="border border-gray-200 rounded-md p-4"
                  style={{ height: "900px" }}
                >
                  <div className="flex justify-between gap-2">
                    <div className="font-medium text-md">
                      Available FBOs
                      <span className="bg-gray-100 text-gray-700 ml-2 py-1 px-2 rounded-full text-md font-medium inline-block">
                        {availableFbos.length}
                      </span>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => handleToggleCreateFboModal()}
                        className="inline-flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2
                              text-md font-medium text-white shadow-sm hover:bg-red-700
                              focus:outline-none focus:ring-2 focus:ring-red-500
                              focus:ring-offset-2 sm:w-auto"
                      >
                        Create New FBO
                      </button>
                    </div>
                  </div>

                  <div className="text-md mt-6">
                    {availableFbos.length === 0 && (
                      <div className="text-center m-auto mt-24 text-md">
                        No available FBOs set.
                      </div>
                    )}

                    <div
                      className="overflow-y-auto"
                      style={{ maxHeight: "700px" }}
                    >
                      {availableFbos.map((fbo) => (
                        <div key={fbo.id} className="relative">
                          <ul>
                            <li>
                              <div className="relative flex items-center space-x-3 px-2 py-3 hover:bg-gray-50 rounded-md">
                                <div className="min-w-0 flex-1">
                                  <p className="text-md text-gray-900 font-normal truncate overflow-ellipsis w-60">
                                    {fbo.name}
                                  </p>
                                </div>
                                <div>
                                  <button
                                    type="button"
                                    onClick={() => deleteAvailableFbo(fbo.id)}
                                    className="inline-flex items-center rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isCreateFboModalOpen && (
        <CreateFBOModal
          isOpen={isCreateFboModalOpen}
          handleClose={handleToggleCreateFboModal}
          addFBO={handleCreateFbo}
        />
      )}

      {isAddVendorModalOpen && (
        <AddVendorModal
          isOpen={isAddVendorModalOpen}
          handleClose={handleToggleAddVendorModal}
          addVendor={handleAddVendor}
        />
      )}
    </AnimatedPage>
  );
};

export default AirportDetails;
